import {
  IntegrationConnection,
  IntegrationMatrix,
  ShopIntegrationAccess,
  IntegrationEventCatalog,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEndpointPayload,
  WebhookEndpointWithSecret,
} from '../../types';
import api from './client';

/**
 * Outbound webhooks and provider connections. `shopId` is read only for super
 * admins — a shop admin always acts on their own shop.
 */
export const integrationsService = {
  getEventTypes: async (): Promise<IntegrationEventCatalog> => {
    const response = await api.get<IntegrationEventCatalog>('/integrations/event-types');
    return response.data;
  },
  listWebhooks: async (shopId?: string): Promise<WebhookEndpoint[]> => {
    const response = await api.get<WebhookEndpoint[]>('/integrations/webhooks', {
      params: shopId ? { shopId } : undefined,
    });
    return response.data;
  },
  createWebhook: async (
    payload: WebhookEndpointPayload,
    shopId?: string,
  ): Promise<WebhookEndpointWithSecret> => {
    const response = await api.post<WebhookEndpointWithSecret>(
      '/integrations/webhooks',
      payload,
      { params: shopId ? { shopId } : undefined },
    );
    return response.data;
  },
  updateWebhook: async (
    id: string,
    payload: Partial<WebhookEndpointPayload>,
  ): Promise<WebhookEndpoint> => {
    const response = await api.patch<WebhookEndpoint>(`/integrations/webhooks/${id}`, payload);
    return response.data;
  },
  removeWebhook: async (id: string): Promise<void> => {
    await api.delete(`/integrations/webhooks/${id}`);
  },
  rotateSecret: async (id: string): Promise<WebhookEndpointWithSecret> => {
    const response = await api.post<WebhookEndpointWithSecret>(
      `/integrations/webhooks/${id}/rotate-secret`,
    );
    return response.data;
  },
  sendTest: async (id: string): Promise<{ eventId: string }> => {
    const response = await api.post<{ eventId: string }>(`/integrations/webhooks/${id}/test`);
    return response.data;
  },
  listDeliveries: async (id: string, limit = 50): Promise<WebhookDelivery[]> => {
    const response = await api.get<WebhookDelivery[]>(
      `/integrations/webhooks/${id}/deliveries`,
      { params: { limit } },
    );
    return response.data;
  },
  retryDelivery: async (deliveryId: string): Promise<WebhookDelivery> => {
    const response = await api.post<WebhookDelivery>(
      `/integrations/deliveries/${deliveryId}/retry`,
    );
    return response.data;
  },
  /** Which integrations the platform administrator allows this shop to use. */
  getAccess: async (shopId?: string): Promise<ShopIntegrationAccess[]> => {
    const response = await api.get<ShopIntegrationAccess[]>('/integrations/access', {
      params: shopId ? { shopId } : undefined,
    });
    return response.data;
  },
  listConnections: async (shopId?: string): Promise<IntegrationConnection[]> => {
    const response = await api.get<IntegrationConnection[]>('/integrations/connections', {
      params: shopId ? { shopId } : undefined,
    });
    return response.data;
  },
  disconnect: async (provider: string, shopId?: string): Promise<void> => {
    await api.delete(`/integrations/connections/${encodeURIComponent(provider)}`, {
      params: shopId ? { shopId } : undefined,
    });
  },
};

/** Super admin only: which integrations each shop may use. */
export const platformIntegrationsService = {
  matrix: async (): Promise<IntegrationMatrix> => {
    const response = await api.get<IntegrationMatrix>('/admin/integrations');
    return response.data;
  },
  setForShop: async (
    shopId: string,
    key: string,
    enabled: boolean,
    reason?: string,
  ): Promise<IntegrationMatrix> => {
    const response = await api.put<IntegrationMatrix>(
      `/admin/integrations/shops/${shopId}/${encodeURIComponent(key)}`,
      { enabled, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },
  setForAllShops: async (
    key: string,
    enabled: boolean,
    reason?: string,
  ): Promise<IntegrationMatrix> => {
    const response = await api.put<IntegrationMatrix>(
      `/admin/integrations/${encodeURIComponent(key)}`,
      { enabled, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },
};
